import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading, Text } from '@lnr-v2/ui';
import { AdminLayout } from '../../layouts/AdminLayout';
import { StatCard } from '../../components/molecules';
import { PortfolioViewsModal, NewLeadsModal } from '../../components/organisms';
import { useDashboardStats } from '../../hooks';
import type { Activity } from '@lnr-v2/types';
import styles from './DashboardPage.module.css';

// Helper function to render activity icons
const getActivityIcon = (iconType: Activity['icon']) => {
  const icons = {
    check: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
      </svg>
    ),
    user: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>
    ),
    edit: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M12 20h9"></path>
        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
      </svg>
    ),
    folder: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
      </svg>
    ),
    upload: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
        <polyline points="17 8 12 3 7 8"></polyline>
        <line x1="12" y1="3" x2="12" y2="15"></line>
      </svg>
    ),
    users: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
      </svg>
    ),
  };
  return icons[iconType] || icons.check;
};

// Helper function to format time ago
const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  return `${Math.floor(seconds / 86400)} days ago`;
};

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();

  // Fetch real dashboard data from API
  const { data, loading, error } = useDashboardStats();

  // Modal state
  const [portfolioModalOpen, setPortfolioModalOpen] = useState(false);
  const [leadsModalOpen, setLeadsModalOpen] = useState(false);

  // Quick action handlers
  const handleNewCase = () => {
    navigate('/admin/cases/new');
  };

  const handleAddClient = () => {
    navigate('/admin/clients');
  };

  const handleSettings = () => {
    navigate('/admin/settings');
  };

  const handleUploadMedia = () => {
    // TODO: Implement media upload functionality
    // For now, navigate to cases page
    navigate('/admin/cases');
  };

  // Show loading state
  if (loading) {
    return (
      <AdminLayout>
        <div className={styles.container}>
          <Heading as="h1" className={styles.title}>
            Dashboard
          </Heading>
          <div className={styles.loadingState}>
            <Text>Loading dashboard data...</Text>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Show error state
  if (error) {
    return (
      <AdminLayout>
        <div className={styles.container}>
          <Heading as="h1" className={styles.title}>
            Dashboard
          </Heading>
          <div className={styles.errorState}>
            <Text className={styles.errorMessage}>
              Failed to load dashboard data. Please try again.
            </Text>
            <Text size="sm" className={styles.errorDetails}>
              {error.message}
            </Text>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // No data state (shouldn't happen, but handle it)
  if (!data) {
    return (
      <AdminLayout>
        <div className={styles.container}>
          <Heading as="h1" className={styles.title}>
            Dashboard
          </Heading>
          <Text>No dashboard data available.</Text>
        </div>
      </AdminLayout>
    );
  }

  // Generate mock sparkline data based on real values
  // In Phase 2, these will come from the API
  const generateSparkline = (value: number) => {
    return Array.from({ length: 7 }, () =>
      Math.round(value * (0.85 + Math.random() * 0.3))
    );
  };

  return (
    <AdminLayout>
      <div className={styles.container}>
        <Heading as="h1" className={styles.title}>
          Dashboard
        </Heading>

        <Text className={styles.description}>
          Welcome to the admin dashboard. Here is a real-time overview of your
          agency performance.
        </Text>

        {/* Dashboard stats with REAL data */}
        <div className={styles.statsGrid}>
          <StatCard
            label="Total Cases"
            value={data.kpis.totalCases.toString()}
            subtitle="Portfolio projects"
            variant="primary"
            trend="up"
            trendValue="12%"
            glass
            hero
            sparklineData={generateSparkline(data.kpis.totalCases)}
            icon={
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
              </svg>
            }
          />

          <StatCard
            label="Active Projects"
            value={data.kpis.activeProjects.toString()}
            subtitle="In progress"
            variant="success"
            trend="up"
            trendValue="8%"
            glass
            sparklineData={generateSparkline(data.kpis.activeProjects)}
            icon={
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="9" y1="9" x2="15" y2="9"></line>
                <line x1="9" y1="15" x2="15" y2="15"></line>
              </svg>
            }
          />

          <StatCard
            label="Total Clients"
            value={data.kpis.totalClients.toString()}
            subtitle="Active partnerships"
            variant="neutral"
            trend="up"
            trendValue="5%"
            glass
            sparklineData={generateSparkline(data.kpis.totalClients)}
            icon={
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M3 21h18"></path>
                <path d="M19 21V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v14"></path>
                <rect x="9" y="9" width="4" height="4"></rect>
                <rect x="9" y="15" width="4" height="2"></rect>
              </svg>
            }
          />

          <StatCard
            label="Portfolio Views"
            value={data.kpis.portfolioViewsThisMonth.toString()}
            subtitle="This month"
            variant="info"
            trend="up"
            trendValue="12%"
            glass
            sparklineData={generateSparkline(data.kpis.portfolioViewsThisMonth)}
            onClick={() => setPortfolioModalOpen(true)}
            icon={
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
            }
          />

          <StatCard
            label="New Leads"
            value={data.kpis.newLeadsThisMonth.toString()}
            subtitle="This month"
            variant="success"
            trend="up"
            trendValue="15%"
            glass
            sparklineData={generateSparkline(data.kpis.newLeadsThisMonth)}
            onClick={() => setLeadsModalOpen(true)}
            icon={
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="8.5" cy="7" r="4"></circle>
                <line x1="20" y1="8" x2="20" y2="14"></line>
                <line x1="23" y1="11" x2="17" y2="11"></line>
              </svg>
            }
          />
        </div>

        {/* Quick Actions Panel */}
        <div className={styles.quickActions}>
          <button
            className={styles.quickActionButton}
            type="button"
            onClick={handleNewCase}
            aria-label="Create new case"
          >
            <div className={`${styles.quickActionIcon} ${styles.primary}`}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="16"></line>
                <line x1="8" y1="12" x2="16" y2="12"></line>
              </svg>
            </div>
            <span className={styles.quickActionLabel}>New Case</span>
          </button>

          <button
            className={styles.quickActionButton}
            type="button"
            onClick={handleUploadMedia}
            aria-label="Upload media"
          >
            <div className={`${styles.quickActionIcon} ${styles.success}`}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
            </div>
            <span className={styles.quickActionLabel}>Upload Media</span>
          </button>

          <button
            className={styles.quickActionButton}
            type="button"
            onClick={handleAddClient}
            aria-label="Add new client"
          >
            <div className={`${styles.quickActionIcon} ${styles.warning}`}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <span className={styles.quickActionLabel}>Add Client</span>
          </button>

          <button
            className={styles.quickActionButton}
            type="button"
            onClick={handleSettings}
            aria-label="Open settings"
          >
            <div className={`${styles.quickActionIcon} ${styles.neutral}`}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M12 1v6m0 6v6m5.2-14.8l-4.2 4.2m-2 2l-4.2 4.2m11-1.2h-6m-6 0H1m14.8 5.2l-4.2-4.2m-2-2l-4.2-4.2"></path>
              </svg>
            </div>
            <span className={styles.quickActionLabel}>Settings</span>
          </button>
        </div>

        {/* REAL-TIME activity feed from database */}
        <div className={styles.section}>
          <Heading as="h2" className={styles.sectionTitle}>
            Recent Activity
          </Heading>
          <div className={styles.activityList}>
            {data.activities.length > 0 ? (
              data.activities.map((activity) => (
                <div key={activity.id} className={styles.activityItem}>
                  <div className={styles.activityIcon}>
                    {getActivityIcon(activity.icon)}
                  </div>
                  <div className={styles.activityContent}>
                    <Text size="sm">{activity.message}</Text>
                    <Text size="xs" className={styles.activityTime}>
                      {formatTimeAgo(activity.timestamp)}
                    </Text>
                  </div>
                </div>
              ))
            ) : (
              <Text size="sm" className={styles.emptyState}>
                No recent activity
              </Text>
            )}
          </div>
        </div>

        {/* Top Viewed Cases Section */}
        {data.topCases.length > 0 && (
          <div className={styles.topCasesSection}>
            <Heading as="h2" className={styles.sectionTitle}>
              Top Viewed Cases
            </Heading>
            <div className={styles.topCasesGrid}>
              {data.topCases.map((caseItem) => (
                <div key={caseItem.id} className={styles.topCaseCard}>
                  {caseItem.image && (
                    <div className={styles.topCaseImage}>
                      <img src={caseItem.image} alt={caseItem.title} />
                    </div>
                  )}
                  <div className={styles.topCaseContent}>
                    <Text
                      size="sm"
                      weight="semibold"
                      className={styles.topCaseTitle}
                    >
                      {caseItem.title}
                    </Text>
                    <Text size="xs" className={styles.topCaseClient}>
                      {caseItem.clientName}
                    </Text>
                    <div className={styles.topCaseViews}>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                      <Text size="xs">
                        {caseItem.viewCount.toLocaleString()} views
                      </Text>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Portfolio Views Modal */}
      <PortfolioViewsModal
        isOpen={portfolioModalOpen}
        onClose={() => setPortfolioModalOpen(false)}
        totalViews={data.kpis.portfolioViewsThisMonth}
        topCases={data.topCases}
        trend={{ direction: 'up', value: '12%', label: 'vs last month' }}
      />

      {/* New Leads Modal */}
      <NewLeadsModal
        isOpen={leadsModalOpen}
        onClose={() => setLeadsModalOpen(false)}
        totalLeads={data.kpis.newLeadsThisMonth}
        leads={[]}
        trend={{ direction: 'up', value: '15%', label: 'vs last month' }}
      />
    </AdminLayout>
  );
};
